import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquareCode, Send, Bot, User, Upload, ExternalLink, FlaskConical, RotateCcw, Loader2 } from "lucide-react";

export default function Simulatore() {
  const { t } = useTranslation();
  const PRESETS = [t("sim.p1"), t("sim.p2"), t("sim.p3"), t("sim.p4"), t("sim.p5")];
  const [phone, setPhone] = useState("+39 333 9990001");
  const [name, setName] = useState("Cliente Test");
  const [text, setText] = useState("");
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [lastConv, setLastConv] = useState(null);
  const nav = useNavigate();

  // Recovers the AI reply already saved in the Inbox when the HTTP call timed out / failed mid-way.
  const syncFromInbox = async (msg) => {
    try {
      const { data } = await http.get("/conversations", { timeout: 15000 });
      const digits = phone.replace(/\D/g, "").slice(-9);
      const conv = (Array.isArray(data) ? data : data.items || []).find((c) => String(c.customer?.phone || c.customer_phone || "").replace(/\D/g, "").endsWith(digits));
      if (!conv) return false;
      const full = (await http.get(`/conversations/${conv.id}`, { timeout: 15000 })).data;
      const msgs = full.messages || [];
      const idx = msgs.map((m) => m.text).lastIndexOf(msg);
      if (idx === -1) return false; // the message never reached the server: nothing to recover
      const reply = msgs.slice(idx + 1).find((m) => m.direction === "out" || m.sender === "ai" || m.role === "assistant");
      setLastConv(conv.id);
      if (reply) { setLog((l) => [...l, { who: "ai", text: reply.text }]); return true; }
    } catch { /* keep UI usable */ }
    return false;
  };

  const send = async (msgArg) => {
    const msg = msgArg ?? text;
    if (!msg.trim() || busy) return;
    setBusy(true);
    setLog((l) => [...l, { who: "customer", text: msg }]);
    setText("");
    try {
      const r = await http.post("/simulator/message", { phone, text: msg, name }, { timeout: 45000 });
      setLastConv(r.data.conversation_id);
      if (r.data.reply) setLog((l) => [...l, { who: "ai", text: r.data.reply, handoff: r.data.handoff }]);
      else setLog((l) => [...l, { who: "sys", text: `${t("sim.no_reply")} ${t("sim.no_reply_hint")}` }]);
    } catch (e) {
      const recovered = await syncFromInbox(msg);
      if (!recovered) { toast.error(apiErr(e)); setLog((l) => [...l, { who: "sys", text: t("sim.send_failed", "Invio non completato: controlla l'Inbox e riprova.") }]); }
    } finally { setBusy(false); }
  };

  const newConversation = () => {
    const n = String(Math.floor(1000000 + Math.random() * 8999999));
    setPhone(`+39 333 ${n}`);
    setLog([]); setLastConv(null); setText("");
    toast.success(t("sim.new_started"));
  };

  const reactivateAi = async () => {
    try {
      await http.post("/simulator/reset", { phone });
      setLog((l) => [...l, { who: "sys", text: t("sim.ai_reactivated") }]);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sendImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    setLog((l) => [...l, { who: "customer", text: `📷 ${t("sim.photo_sent")}` }]);
    const fd = new FormData(); fd.append("file", f); fd.append("phone", phone); fd.append("name", name);
    try { const r = await http.post("/simulator/image", fd); setLastConv(r.data.conversation_id); setLog((l) => [...l, { who: "sys", text: t("sim.photo_received") }]); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5" data-testid="simulator-panel">
      <div className="flex items-center gap-2">
        <MessageSquareCode className="w-6 h-6 text-sky-600" />
        <h1 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("sim.title")}</h1>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
        <FlaskConical className="w-4 h-4 mt-0.5 shrink-0" /> {t("sim.dev_note")}
      </div>

      <Card className="p-4 border-slate-200 grid sm:grid-cols-2 gap-3">
        <div><Label htmlFor="sim-phone">{t("sim.phone")}</Label><Input id="sim-phone" data-testid="sim-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" /></div>
        <div><Label htmlFor="sim-name">{t("sim.name")}</Label><Input id="sim-name" data-testid="sim-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="sim-new-conversation-btn" onClick={newConversation} className="gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> {t("sim.new_conversation")}</Button>
          <Button type="button" variant="ghost" size="sm" data-testid="sim-reactivate-ai-btn" onClick={reactivateAi} className="gap-1.5 text-sky-700"><Bot className="w-3.5 h-3.5" /> {t("sim.reactivate_ai")}</Button>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((p, i) => <button key={i} onClick={() => send(p)} disabled={busy} className="text-xs bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 text-slate-600">{p}</button>)}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="h-80 overflow-y-auto scrollbar-thin p-4 space-y-2 bg-slate-50">
          {log.length === 0 && <div className="text-sm text-slate-400 text-center py-10">{t("sim.empty")}</div>}
          {log.map((m, i) => (
            <div key={i} className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.who === "customer" ? "chat-bubble-in mr-auto" : m.who === "ai" ? "chat-bubble-ai ml-auto" : "bg-slate-200 text-slate-600 mx-auto text-xs"}`}>
              {m.who !== "sys" && <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5 flex items-center gap-1 text-slate-500">{m.who === "customer" ? <><User className="w-3 h-3" /> {t("sim.customer")}</> : <><Bot className="w-3 h-3" /> AI</>}</div>}
              <div className="whitespace-pre-wrap text-[#0F172A]">{m.text}</div>
              {m.handoff && <div className="text-[10px] text-red-600 font-semibold mt-1">→ {t("sim.requires_op")}</div>}
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-slate-200 flex gap-2 bg-white">
          <label><input type="file" accept="image/*" className="hidden" data-testid="sim-image-input" onChange={sendImage} /><Button variant="outline" size="icon" className="h-11 w-11" asChild><span><Upload className="w-4 h-4" /></span></Button></label>
          <Input data-testid="sim-text" placeholder={t("sim.placeholder")} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !busy && send()} className="h-11" />
          <Button data-testid="sim-send-btn" onClick={() => send()} disabled={busy} data-busy={busy ? "1" : "0"} className="h-11 bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
        </div>
      </Card>

      {lastConv && <Button variant="outline" className="gap-1.5" data-testid="open-conv-btn" onClick={() => nav(`/inbox/${lastConv}`)}><ExternalLink className="w-4 h-4" /> {t("sim.open_conv")}</Button>}
    </div>
  );
}
